import { Temporal } from "temporal-polyfill";
import {
  BaseFeeTiming,
  BillingPlanStatus,
  BillingPricingMode,
  OrganizationBillingCategoryCode,
} from "@api/shared-runtime/enums";
import type { RouterInputs, RouterOutputs } from "@/trpc";

export type BillingPlanData = RouterOutputs["adminBillingPlans"]["getPlan"];
export type BillingPlanPreview =
  RouterOutputs["adminBillingPlans"]["previewPlan"];
export type BillingPlanInput = RouterInputs["adminBillingPlans"]["previewPlan"];
export type BillingPlanCategory = BillingPlanData["categories"][number];
export type BillingPlanVersion = BillingPlanData["plans"][number];

export const BILLING_TIMEZONE = "America/New_York";
export const MONEY_STEP = "0.01";
export const PERCENT_STEP = "0.0001";
export const WHOLE_QUANTITY_STEP = "1";
export const FREE_CATEGORY_LABEL = "Free";
export const PRICING_UNAVAILABLE_LABEL = "Pricing unavailable";
export const DEFAULT_DOCUMENT_SIGNATURE_FEE = "0.30";
export const DOCUMENT_SIGNATURE_COST = "0.25";

export const BASE_FEE_TIMING_LABELS: Record<BaseFeeTiming, string> = {
  [BaseFeeTiming.InAdvance]: "Base fee billed at the start of each cycle",
  [BaseFeeTiming.InArrears]: "Base fee billed after each cycle ends",
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export type BillingPlanFormValues = {
  startDate: string;
  baseMonthlyAmount: string;
  baseFeeTiming: BaseFeeTiming;
  invoicingEnabled: boolean;
  wagePassThroughEnabled: boolean;
  notes: string;
  prices: {
    categoryCode: BillingPlanCategory["categoryCode"];
    pricingMode: BillingPricingMode;
    includedQuantity: string;
    unitAmount: string;
    basisPercent: string;
  }[];
};

export function formatPlanCurrency(amount: string): string {
  return currencyFormatter.format(Number(amount));
}

export function firstDayOfMonth(month: string): string {
  return `${month}-01`;
}

/** A fee is worth collecting up front; a usage-only plan has nothing to bill until it is used. */
export function getDefaultBaseFeeTiming(
  baseMonthlyAmount: string,
): BaseFeeTiming {
  return Number(baseMonthlyAmount) > 0
    ? BaseFeeTiming.InAdvance
    : BaseFeeTiming.InArrears;
}

/** Inclusive service dates of a half-open invoice window, in New York. */
export function formatInvoicePeriod(period: {
  start: string;
  end: string;
}): string {
  return `${formatPlanInstant(period.start)} – ${formatPlanDate(getLastServiceDate(period.end))}`;
}

export function getSelectedCategoryRate(price: {
  pricingMode: BillingPricingMode | null;
  unitAmount: string | null;
  basisPercent: string | null;
}): string | null {
  if (price.pricingMode === BillingPricingMode.PercentOfBasis) {
    return price.basisPercent;
  }
  if (price.pricingMode === BillingPricingMode.PerUnit) {
    return price.unitAmount;
  }
  return null;
}

export function isFreeCategoryRate(rate: string | null): boolean {
  return rate === null || rate.trim() === "" || Number(rate) === 0;
}

export function getIncludedQuantityLabel(unitNoun: string): string {
  return `Free ${unitNoun}s per billing cycle`;
}

/** A blank or malformed allowance is treated as zero, matching what save would reject. */
function parseIncludedQuantity(includedQuantity: string): number {
  const quantity = Number(includedQuantity);
  if (!Number.isInteger(quantity) || quantity < 0) {
    return 0;
  }
  return quantity;
}

export function describeCategoryStatus({
  category,
  price,
}: {
  category: Pick<BillingPlanCategory, "supported" | "unitNoun">;
  price: BillingPlanFormValues["prices"][number];
}): string {
  if (!category.supported) {
    return PRICING_UNAVAILABLE_LABEL;
  }
  const rate = getSelectedCategoryRate(price);
  if (isFreeCategoryRate(rate)) {
    return FREE_CATEGORY_LABEL;
  }
  if (price.pricingMode === BillingPricingMode.PercentOfBasis) {
    return `${Number(rate)}% of wages`;
  }
  const perUnit = `${formatPlanCurrency(price.unitAmount)} per ${category.unitNoun}`;
  const included = parseIncludedQuantity(price.includedQuantity);
  if (included === 0) {
    return perUnit;
  }
  return `${included} free, then ${perUnit}`;
}

export function describeIncludedQuantity({
  includedQuantity,
  unitAmount,
  unitNoun,
}: {
  includedQuantity: string;
  unitAmount: string;
  unitNoun: string;
}): string {
  const fee = formatPlanCurrency(unitAmount);
  const included = parseIncludedQuantity(includedQuantity);
  if (included === 0) {
    return `No free ${unitNoun}s. Every ${unitNoun} is billed at ${fee}.`;
  }
  if (included === 1) {
    return `The first ${unitNoun} each billing cycle is free, then ${fee} each.`;
  }
  return `The first ${included} ${unitNoun}s each billing cycle are free, then ${fee} each.`;
}

export function formatPlanDate(date: string): string {
  return Temporal.PlainDate.from(date).toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatPlanInstant(instant: string): string {
  const date = Temporal.Instant.from(instant)
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate();
  return formatPlanDate(date.toString());
}

export function getLastServiceDate(exclusiveEnd: string): string {
  // Plan ends are exclusive instants. Subtracting an instant, rather than a
  // calendar day, also handles a same-day replacement that starts immediately.
  return Temporal.Instant.from(exclusiveEnd)
    .subtract({ nanoseconds: 1 })
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate()
    .toString();
}

export function getPlanGap({
  data,
  startDate,
}: {
  data: BillingPlanData;
  startDate: string;
}): { firstDate: string; lastDate: string } | null {
  const current = data.plans.find(
    (plan) => plan.status === BillingPlanStatus.Active,
  );
  if (!startDate || !current?.effectiveEndsAt) {
    return null;
  }
  const firstDate = Temporal.Instant.from(current.effectiveEndsAt)
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate()
    .toString();
  if (startDate <= firstDate) {
    return null;
  }
  return {
    firstDate,
    lastDate: Temporal.PlainDate.from(startDate)
      .subtract({ days: 1 })
      .toString(),
  };
}

export function getPlanFormDefaults(
  data: BillingPlanData,
): BillingPlanFormValues {
  const current = data.plans.find(
    (plan) => plan.status === BillingPlanStatus.Active,
  );
  const baseMonthlyAmount = current?.baseMonthlyAmount ?? "0.00";
  return {
    startDate: data.defaultStartDate,
    baseMonthlyAmount,
    baseFeeTiming:
      current?.baseFeeTiming ?? getDefaultBaseFeeTiming(baseMonthlyAmount),
    // A replacement keeps the current plan's choice; a first plan is invoiced.
    invoicingEnabled: current ? current.invoicingStartsAt !== null : true,
    // Most units have Saile pay their providers, so a first plan passes wages through.
    wagePassThroughEnabled: current?.wagePassThroughEnabled ?? true,
    notes: current?.notes ?? "",
    prices: data.categories.map((category) => {
      const price = current?.prices.find(
        (item) => item.billingCategoryId === category.categoryCode,
      );
      let unitAmount = price?.unitAmount ?? "";
      // Defaults apply only to new plans. A missing price on an active plan
      // is an existing free agreement and must remain free when replaced.
      if (
        !current &&
        category.supported &&
        category.categoryCode ===
          OrganizationBillingCategoryCode.DocumentSignature &&
        category.pricingModes.includes(BillingPricingMode.PerUnit)
      ) {
        unitAmount = DEFAULT_DOCUMENT_SIGNATURE_FEE;
      }
      return {
        categoryCode: category.categoryCode,
        pricingMode: price?.pricingMode ?? BillingPricingMode.PerUnit,
        includedQuantity: String(price?.includedQuantity ?? 0),
        unitAmount,
        basisPercent: price?.basisPercent ?? "",
      };
    }),
  };
}

export function getPlanPreviewInput({
  data,
  values,
}: {
  data: BillingPlanData;
  values: BillingPlanFormValues;
}): BillingPlanInput {
  const supportedCodes = new Set(
    data.categories
      .filter((category) => category.supported)
      .map((category) => category.categoryCode),
  );
  return {
    organizationBusinessUnitId: data.unit.id,
    expectedVersion: data.version,
    startDate: values.startDate,
    baseMonthlyAmount: values.baseMonthlyAmount,
    baseFeeTiming: values.baseFeeTiming,
    invoicingEnabled: values.invoicingEnabled,
    wagePassThroughEnabled: values.wagePassThroughEnabled,
    notes: values.notes.trim() || null,
    prices: values.prices
      .filter(
        (price) =>
          supportedCodes.has(price.categoryCode) &&
          (getSelectedCategoryRate(price)?.trim() ?? "") !== "",
      )
      .map((price) => {
        if (price.pricingMode === BillingPricingMode.PercentOfBasis) {
          return {
            categoryCode: price.categoryCode,
            pricingMode: price.pricingMode,
            includedQuantity: 0,
            unitAmount: null,
            basisPercent: price.basisPercent,
          };
        }
        return {
          categoryCode: price.categoryCode,
          pricingMode: price.pricingMode,
          includedQuantity: Number(price.includedQuantity),
          unitAmount: price.unitAmount,
          basisPercent: null,
        };
      }),
  };
}
